// The newsprint page for The Bull Sheet — presentational only, shared by the
// authenticated view and the public reading room.
import type { ReactNode } from "react";
import { px as pxFor, signedUsd } from "../lib/money";
import { ConsensusStrip, PositionLadder, TapeChart } from "./NewsCharts";
import { readMoney } from "./MoneyScope";

// Per-article provenance (docs/BULL_SHEET_SOURCING.md): resolved citations,
// self-declared + audited model recall, and the editorial lineage that
// answers "why is this article written as is".
export type SourceRow = { n: number; mark: string; label: string; url?: string | null; ids: string[] };
export type UnitSources = {
  rows: SourceRow[]; dropped: number; recall: string[];
  editorial?: { voice: string; instruction?: string; editor?: string; lead_angle?: string };
  recall_audit?: { uncited_claims: string[]; recall_share: number };
};
export type Col = { section: string; headline: string; body: string; byline?: string;
                    slug?: string; sources?: UnitSources };
export type TapeRow = { label: string; d1?: number | null; d5?: number | null; d20?: number | null };
export type EditionIssue = { id: number; volume: number; number: number; label: string; share_url: string; app_url: string; pressed_at?: string | null };
type IntelCard = { symbol: string; shape?: string; chart?: string | null; structure?: number; entry?: number; commentary?: string;
  prediction?: { text: string; confirms?: string; contradicts?: string; direction?: string } | null };
export type Edition = {
  masthead: string; tagline: string; date: string; edition_time: string;
  issue?: EditionIssue;
  sources: { outlets: number; headlines: number; earnings: number; sectors: number };
  source_manifest?: { available?: Record<string, number>; legend?: Record<string, string> };
  regime: string;
  lead: { headline: string; body: string; slug?: string; sources?: UnitSources };
  columns: Col[];
  tape?: TapeRow[];
  local_desk?: {
    headline: string; body: string; record?: string;
    items?: { scope: string; symbol: string; status: string; note: string }[];
  };
  asset_review?: { assets: AssetReview[] };
  trade_of_the_day?: TradeOfTheDay;
  intel_cards?: IntelCard[];
  intel_charts?: { market?: string; quadrant?: string };
  angels_and_demons?: { angel: { headline: string; body: string }; demon: { headline: string; body: string } };
  gpop_corner?: { headline: string; body: string; told_you_so?: boolean };
  stonky_take: { headline: string; body: string; slug?: string; sources?: UnitSources };
};

// ── Sourcing rendering ──────────────────────────────────────────────────────
// Inline [[n]] tokens become superscripts; each article closes with a subtle
// printer's-mark line that expands to the full source table. The marks are
// the paper's seven-glyph legend, printed once at the back.

// fock.space's own favicon (their inline SVG, embedded verbatim as a data
// URI): the graph's mark in the sourcing UI is their planet, per Alan —
// embedded, not fetched, so it renders even when fock.space is unreachable.
const FOCK_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' rx='6' fill='%230a1526'/%3E%3Ccircle cx='15' cy='16.5' r='9' fill='none' stroke='%23C2A464' stroke-width='3.2'/%3E%3Ccircle cx='15' cy='16.5' r='4.2' fill='%23C2A464'/%3E%3Ccircle cx='25' cy='8' r='3' fill='%235DB4FF'/%3E%3C/svg%3E";

function Mark({ mark, size = "w-3 h-3" }: { mark: string; size?: string }) {
  if (mark === "⬡") {
    return <img src={FOCK_ICON} alt="fock.space" title="fock.space graph"
                className={`inline ${size} align-baseline`} />;
  }
  return <>{mark}</>;
}

function Cited({ text, sources }: { text: string; sources?: UnitSources }) {
  const parts = (text || "").split(/\[\[(\d+)\]\]/g);
  if (parts.length === 1) return <>{text}</>;
  const byN = new Map((sources?.rows ?? []).map((r) => [String(r.n), r]));
  return (
    <>
      {parts.map((p, i) => {
        if (i % 2 === 0) return <span key={i}>{p}</span>;
        const r = byN.get(p);
        const sup = (
          <sup className="text-[9px] font-bold whitespace-nowrap" style={{ color: "#8a6d3b" }} title={r?.label ?? ""}>
            {r?.mark === "⬡" && <img src={FOCK_ICON} alt="" className="inline w-2 h-2 mr-px align-baseline" />}
            {p}
          </sup>
        );
        return r?.url
          ? <a key={i} href={r.url} target="_blank" rel="noreferrer">{sup}</a>
          : <span key={i}>{sup}</span>;
      })}
    </>
  );
}

function AnalysisSlug({ slug }: { slug?: string }) {
  if (!slug) return null;
  return (
    <span className="ml-2 px-1.5 py-px text-[9px] uppercase tracking-widest font-bold border align-middle"
          style={{ color: "#6b6355", borderColor: "#b7ae98" }}
          title="The sourcing audit found this piece rests mostly on model background rather than the day's feeds.">
      {slug}
    </span>
  );
}

function SourcedFooter({ s }: { s?: UnitSources }) {
  if (!s) return null;
  const counts: Record<string, number> = {};
  for (const r of s.rows ?? []) counts[r.mark] = (counts[r.mark] ?? 0) + 1;
  const recallN = (s.recall?.length ?? 0) + (s.recall_audit?.uncited_claims?.length ?? 0);
  const bits: ReactNode[] = Object.entries(counts).map(([m, n]) => (
    <span key={m}><Mark mark={m} size="w-2.5 h-2.5" /> {n}</span>
  ));
  if (s.editorial?.voice && s.editorial.voice !== "editor") bits.push(<span key="v">✍ {s.editorial.voice}</span>);
  if (recallN) bits.push(<span key="o">○ {recallN}</span>);
  if (!bits.length && !s.editorial) return null;
  return (
    <details className="mt-1.5">
      <summary className="cursor-pointer list-none text-[9.5px] uppercase tracking-widest select-none"
               style={{ color: "#8a8271" }}>
        sourced{bits.map((bt, i) => <span key={i}> · {bt}</span>)}
      </summary>
      <div className="mt-1 pt-1 border-t space-y-0.5 text-[11px]" style={{ borderColor: "#e6dfcc", color: "#4a4436" }}>
        {(s.rows ?? []).map((r) => (
          <div key={r.n} className="flex gap-1.5">
            <span className="w-8 shrink-0 font-bold">{r.n} <Mark mark={r.mark} /></span>
            {r.url
              ? <a className="underline decoration-dotted" href={r.url} target="_blank" rel="noreferrer">{r.label} ↗</a>
              : <span>{r.label}</span>}
          </div>
        ))}
        {(s.recall ?? []).map((t, i) => (
          <div key={`r${i}`} className="flex gap-1.5">
            <span className="w-8 shrink-0 font-bold">○</span>
            <span className="italic">{t} — writer&rsquo;s background, not today&rsquo;s feeds</span>
          </div>
        ))}
        {(s.recall_audit?.uncited_claims ?? []).map((t, i) => (
          <div key={`a${i}`} className="flex gap-1.5">
            <span className="w-8 shrink-0 font-bold">○</span>
            <span className="italic">{t} — flagged by the independent sourcing audit</span>
          </div>
        ))}
        {s.editorial && (
          <div className="flex gap-1.5">
            <span className="w-8 shrink-0 font-bold">✍</span>
            <span>
              voice: {s.editorial.voice}
              {s.editorial.lead_angle ? ` — the publisher's angle: "${s.editorial.lead_angle}"` : ""}
              {s.editorial.instruction ? ` — "${s.editorial.instruction}"` : ""}
              {s.editorial.editor ? ` · set by ${s.editorial.editor.split("/").pop()}` : ""}
            </span>
          </div>
        )}
      </div>
    </details>
  );
}

function LegendBox({ legend }: { legend?: Record<string, string> }) {
  if (!legend || !Object.keys(legend).length) return null;
  return (
    <div className="mt-4 p-3 border" style={{ background: "#f3efe1", borderColor: "#cfc6b0" }}>
      <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: "#6b6355" }}>
        Key to Sources
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5 text-[11px]" style={{ color: "#4a4436" }}>
        {Object.entries(legend).map(([mark, meaning]) => (
          <div key={mark} className="flex gap-1.5">
            <span className="w-6 shrink-0 font-bold"><Mark mark={mark} /></span>
            <span>{meaning}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// The day's best trade, followed end to end — the one section that proves the
// governance is real rather than decorative, because you can walk it from the
// macro read that started it to the post-mortem that judged it.
export type GNodeLite = { id: string; type: string; label: string };
export type GEdgeLite = { src: string; rel: string; dst: string };
export type TradeOfTheDay = {
  trading_day: string;
  money: "real" | "paper";
  why_selected: string;
  identity: {
    symbol: string; side: string; qty: number; book: string;
    entry_price: number; exit_price: number; pnl: number; pnl_pct: number;
    result?: string; hold_min: number | null; exit_reason?: string | null;
    entry_time?: string | null; exit_time?: string | null;
  };
  summary: { headline: string; points: string[]; flags: string[] } | null;
  branching?: {
    fan_in_same_exit?: { count: number };
    session_fanout?: { trades: number; net_pnl: number; wins: number; losses: number } | null;
    directive_fanout?: { seq: number; trades: number; net_pnl: number }[];
  };
  network: { nodes: GNodeLite[]; edges: GEdgeLite[]; by_type: Record<string, number> };
};

export type TrancheNote = { tranche: string; book: string; letter: string; stance: string; note: string };
export type Opinion = { seat: string; domain: string; stance: string; note: string; tranche_notes?: TrancheNote[] };
// One rung of the asset — its own book, entry rule, levels and P&L. The
// dossier is one entry per ASSET (2026-08-19); these are what it's made of.
export type DossierTranche = {
  tranche_id: string; book: string; entry_rule: string; strike?: number | null; side: string; qty: number;
  entry: number; exit?: number; target?: number | null; stop?: number | null; pnl_pct?: number | null;
  realized_pnl?: number | null; held_days?: number | null; thesis?: string; pct_to_stop?: number;
};
export type AssetReview = {
  symbol: string; book: string; money: string; asset_class: string; state: string;
  side?: string; entry?: number; price?: number; exit?: number; qty?: number;
  pnl_pct?: number; realized_pnl?: number; target?: number; stop?: number;
  targets?: number[]; stops?: number[];
  pct_to_stop?: number; pct_to_target?: number; held_days?: number; thesis?: string;
  opened_at?: string; closed_at?: string;
  regime_at_entry?: string; regime_at_exit?: string; plan_at_entry?: string;
  tranches?: DossierTranche[];
  opinions: Opinion[]; contested?: boolean; split?: boolean;
};

const STANCE: Record<string, { c: string; mark: string; label: string }> = {
  supports: { c: "#1f6f3f", mark: "✓", label: "SUPPORTS" },
  contradicts: { c: "#8a3324", mark: "✗", label: "CONTRADICTS" },
  "no bearing": { c: "#9a9384", mark: "–", label: "NO BEARING" },
};

// Local Desk status → newsprint accent. Warm/urgent for a threatened position,
// cool for one that's working.
const STATUS_TONE: Record<string, { c: string; label: string }> = {
  "near stop": { c: "#8a3324", label: "NEAR STOP" },
  "thesis challenged": { c: "#8a3324", label: "THESIS CHALLENGED" },
  "near target": { c: "#1f6f3f", label: "NEAR TARGET" },
  "thesis intact": { c: "#1f6f3f", label: "THESIS INTACT" },
  watching: { c: "#6b6355", label: "WATCHING" },
  flat: { c: "#6b6355", label: "FLAT" },
};

// ── TRADE OF THE DAY ────────────────────────────────────────────────────────
// Node types laid out by CAUSAL DEPTH, so the diagram reads left-to-right as
// the decision actually happened: what we knew, who decided, what they
// instructed, what was proposed, what hit the market, what we concluded.
const CHAIN_LAYERS: { title: string; types: string[] }[] = [
  { title: "What we knew", types: ["MacroBrief", "SectorDigest", "TriggerEvent"] },
  { title: "Who decided", types: ["BoardSession", "Perspective"] },
  { title: "What was ordered", types: ["Directive", "SeniorRuling", "Constitution"] },
  { title: "What was proposed", types: ["Proposal"] },
  { title: "What hit the market", types: ["Order", "Fill"] },
  { title: "What we concluded", types: ["PostMortem", "TrendInsight"] },
];

function CausalNetwork({ network }: { network: TradeOfTheDay["network"] }) {
  const nodes = network?.nodes ?? [];
  if (!nodes.length) return null;
  const edges = network?.edges ?? [];

  // Bucket every node into its causal layer; anything unrecognised lands in a
  // trailing column rather than being silently dropped.
  const known = new Set(CHAIN_LAYERS.flatMap((l) => l.types));
  const cols = CHAIN_LAYERS.map((l) => ({
    title: l.title,
    nodes: nodes.filter((n) => l.types.includes(n.type)),
  })).filter((c) => c.nodes.length);
  const rest = nodes.filter((n) => !known.has(n.type));
  if (rest.length) cols.push({ title: "Also linked", nodes: rest });

  const W = 900, COL = W / Math.max(cols.length, 1), ROW = 46, PAD = 34;
  const H = PAD * 2 + ROW * Math.max(...cols.map((c) => c.nodes.length), 1);
  const pos = new Map<string, { x: number; y: number }>();
  cols.forEach((c, ci) => {
    c.nodes.forEach((n, ri) => {
      pos.set(n.id, { x: ci * COL + COL / 2, y: PAD + ri * ROW + ROW / 2 });
    });
  });

  return (
    <div style={{ border: "1px solid #cfc7b6", background: "#fbf8f0", padding: "10px 6px" }}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-center mb-1" style={{ color: "#6b6355" }}>
        The causal network behind it · {nodes.length} nodes · {edges.length} links
      </div>
      <div style={{ overflowX: "auto" }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: 560, display: "block" }}>
          {/* column headings */}
          {cols.map((c, ci) => (
            <text key={`h${ci}`} x={ci * COL + COL / 2} y={14} textAnchor="middle"
                  style={{ fontSize: 9, letterSpacing: 1, textTransform: "uppercase", fill: "#8c8478" }}>
              {c.title}
            </text>
          ))}
          {/* edges first, so nodes sit on top */}
          {edges.map((e, i) => {
            const a = pos.get(e.src), b = pos.get(e.dst);
            if (!a || !b) return null;
            const mx = (a.x + b.x) / 2;
            return (
              <path key={`e${i}`} d={`M ${a.x} ${a.y} C ${mx} ${a.y}, ${mx} ${b.y}, ${b.x} ${b.y}`}
                    fill="none" stroke="#b9b0a0" strokeWidth={1} />
            );
          })}
          {nodes.map((n) => {
            const pt = pos.get(n.id);
            if (!pt) return null;
            const label = (n.label || n.type).slice(0, 22);
            return (
              <g key={n.id}>
                <circle cx={pt.x} cy={pt.y} r={5} fill="#2a251c" />
                <text x={pt.x} y={pt.y - 10} textAnchor="middle"
                      style={{ fontSize: 8.5, fill: "#2a251c", fontWeight: 700 }}>{n.type}</text>
                <text x={pt.x} y={pt.y + 15} textAnchor="middle"
                      style={{ fontSize: 8, fill: "#6b6355" }}>{label}</text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function TradeOfTheDaySection({ t }: { t: TradeOfTheDay }) {
  const id = t.identity;
  const win = (id.pnl ?? 0) >= 0;
  const px = (v: number) => pxFor(id.symbol, v);
  const money = signedUsd;

  return (
    <section className="mt-6">
      <div style={{ borderTop: "3px double #2a251c", paddingTop: 10 }}>
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h3 className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "#6b6355" }}>
            Trade of the day · {t.trading_day}
          </h3>
          <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5"
                style={{
                  border: `1px solid ${t.money === "real" ? "#8a3324" : "#6b6355"}`,
                  color: t.money === "real" ? "#8a3324" : "#6b6355",
                }}>
            {t.money === "real" ? "Real money" : "Paper book"}
          </span>
        </div>

        <h2 className="text-2xl sm:text-3xl leading-tight mt-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: "#2a251c" }}>
          {t.summary?.headline ?? `${id.symbol} ${id.side} — ${money(id.pnl)}`}
        </h2>

        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-[12px]" style={{ color: "#4a4438" }}>
          <span><strong>{id.symbol}</strong> {id.side.toUpperCase()} × {id.qty}</span>
          <span>{px(id.entry_price)} → {px(id.exit_price)}</span>
          <span style={{ color: win ? "#1f6f3f" : "#8a3324", fontWeight: 700 }}>
            {money(id.pnl)} ({id.pnl_pct >= 0 ? "+" : ""}{id.pnl_pct}%)
          </span>
          {id.exit_reason && <span>closed by <em>{id.exit_reason}</em></span>}
          <span style={{ color: "#8c8478" }}>{t.why_selected}</span>
        </div>

        {t.summary && t.summary.points.length > 0 && (
          <ul className="mt-3 space-y-1 text-[13px] leading-relaxed" style={{ color: "#2a251c" }}>
            {t.summary.points.map((p, i) => (
              <li key={i} style={{ paddingLeft: 14, textIndent: -14 }}>— {p}</li>
            ))}
          </ul>
        )}

        {t.summary && t.summary.flags.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {t.summary.flags.map((f, i) => (
              <div key={i} className="text-[12px]" style={{ color: "#8a3324" }}>⚠ {f}</div>
            ))}
          </div>
        )}

        <div className="mt-3">
          <CausalNetwork network={t.network} />
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[10px] uppercase tracking-wider"
               style={{ color: "#6b6355" }}>
            {Object.entries(t.network?.by_type ?? {}).map(([k, v]) => (
              <span key={k}>{k} ×{v}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function NewsSheet({ ed }: { ed: Edition | null }) {
  return (
    <div className="rounded-lg overflow-hidden shadow-lg"
         style={{ background: "#f5f1e6", color: "#1a1712", fontFamily: "Georgia, 'Times New Roman', serif" }}>
      <div className="px-6 sm:px-10 py-6">
        <div className="text-center border-b-2 border-black pb-2">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest" style={{ color: "#6b6355" }}>
            <span>Firm Wire</span><span>Est. 2026 · Robot-Edited</span>
          </div>
          <div className="text-4xl sm:text-6xl font-black tracking-tight leading-none mt-1"
               style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {ed?.masthead ?? "THE BULL SHEET"}
          </div>
          <div className="italic text-sm mt-1" style={{ color: "#4a4436" }}>{ed?.tagline}</div>
        </div>
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wider py-1 border-b border-black"
             style={{ color: "#6b6355" }}>
          <span>{ed?.date}</span>
          <span>{ed?.edition_time}</span>
          {ed?.issue && <span className="ml-2 text-slate-500">{ed.issue.label}</span>}
          <span>{ed ? `${ed.sources.outlets} outlets · ${ed.sources.earnings} earnings · ${ed.sources.sectors} sectors` : ""}</span>
        </div>

        {ed?.regime && (
          <p className="text-center italic text-base sm:text-lg my-3" style={{ color: "#2a251c" }}>{ed.regime}</p>
        )}

        {ed?.lead?.headline && (
          <div className="mb-5 pb-4 border-b border-[#cfc6b0]">
            <h2 className="text-2xl sm:text-4xl font-black leading-tight text-center mb-2"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {ed.lead.headline}<AnalysisSlug slug={ed.lead.slug} />
            </h2>
            <p className="text-[15px] leading-relaxed sm:columns-2 sm:gap-8" style={{ textAlign: "justify" }}>
              <span className="float-left text-5xl font-black leading-[0.8] pr-2 pt-1">{firstLetter(ed.lead.body)}</span>
              <Cited text={dropCap(ed.lead.body)} sources={ed.lead.sources} />
            </p>
            <SourcedFooter s={ed.lead.sources} />
          </div>
        )}

        <div className="sm:columns-2 lg:columns-3 sm:gap-8">
          {(ed?.columns ?? []).map((c, i) => (
            <div key={i} className="break-inside-avoid mb-4 pb-3 border-b border-[#cfc6b0]">
              <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: "#8a3324" }}>
                {c.section}<AnalysisSlug slug={c.slug} />
                {c.byline && (
                  <span className="ml-2 normal-case font-normal italic tracking-normal" style={{ color: "#6b6355" }}>
                    · {c.byline}
                  </span>
                )}
              </div>
              <h3 className="text-lg font-bold leading-tight mb-1"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{c.headline}</h3>
              <p className="text-[13.5px] leading-relaxed" style={{ textAlign: "justify" }}>
                <Cited text={c.body} sources={c.sources} />
              </p>
              <SourcedFooter s={c.sources} />
              {/* The tape rides with Markets — the copy's own evidence, so the
                  reader can check the claim instead of taking it on faith. */}
              {c.section === "Markets" && (ed?.tape?.length ?? 0) > 0 && (
                <div className="mt-2 pt-2 border-t border-[#e6dfcc]">
                  <TapeChart rows={ed!.tape!} />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* THE LOCAL DESK — the world above, our own book here. */}
        {ed?.local_desk?.headline && (
          <div className="mt-5 pt-1 border-t-4 border-double border-black">
            <div className="flex items-baseline justify-between flex-wrap gap-x-3">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "#8a3324" }}>
                The Local Desk
              </div>
              <div className="text-[10px] uppercase tracking-widest italic" style={{ color: "#8a8271" }}>
                what today means for our book
              </div>
            </div>
            <h2 className="text-xl sm:text-3xl font-black leading-tight mt-1 mb-2"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {ed.local_desk.headline}
            </h2>
            <p className="text-[14.5px] leading-relaxed sm:columns-2 sm:gap-8 mb-3" style={{ textAlign: "justify" }}>
              {ed.local_desk.body}
            </p>

            {(ed.local_desk.items ?? []).length > 0 && (
              <div className="space-y-0 border-t border-[#cfc6b0]">
                {ed.local_desk.items!.map((it, i) => {
                  const tone = STATUS_TONE[it.status?.toLowerCase()] ?? { c: "#6b6355", label: (it.status || "").toUpperCase() };
                  return (
                    <div key={i} className="py-2 border-b border-[#cfc6b0] flex gap-3">
                      <div className="shrink-0 w-1" style={{ background: tone.c }} />
                      <div className="min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-bold text-[15px]" style={{ fontFamily: "Georgia, serif" }}>{it.symbol}</span>
                          <span className="text-[9px] uppercase tracking-widest font-bold" style={{ color: tone.c }}>
                            {tone.label}
                          </span>
                          <span className="text-[10px] uppercase tracking-wider" style={{ color: "#8a8271" }}>{it.scope}</span>
                        </div>
                        <p className="text-[13px] leading-snug mt-0.5">{it.note}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {ed.local_desk.record && (
              <p className="text-[12.5px] italic mt-3 pl-3 border-l-2" style={{ borderColor: "#8a3324", color: "#4a4436" }}>
                <span className="uppercase tracking-widest text-[9px] font-bold not-italic mr-1.5"
                      style={{ color: "#8a3324" }}>On the record</span>
                {ed.local_desk.record}
              </p>
            )}
          </div>
        )}

        {/* THE BOARD ON THE BOOK — every asset, every seat's verdict. */}
        {(ed?.asset_review?.assets?.length ?? 0) > 0 && (
          <div className="mt-5 pt-1 border-t border-black">
            <div className="flex items-baseline justify-between flex-wrap gap-x-3 mb-2">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "#8a3324" }}>
                The Board on the Book
              </div>
              <div className="text-[10px] uppercase tracking-widest italic" style={{ color: "#8a8271" }}>
                every position · every desk weighs in · &ldquo;no bearing&rdquo; is an answer
              </div>
            </div>
            <div className="space-y-3">
              {ed!.asset_review!.assets.map((a, i) => {
                const live = readMoney(a.money) === "real";
                const closed = a.state === "closed";
                const pnl = closed ? a.realized_pnl : a.pnl_pct;
                const pnlTxt = closed
                  ? (a.realized_pnl != null ? signedUsd(a.realized_pnl) : "—")
                  : (a.pnl_pct != null ? `${a.pnl_pct >= 0 ? "+" : ""}${a.pnl_pct.toFixed(2)}%` : "—");
                const spoke = a.opinions.filter((o) => o.stance !== "no bearing");
                const silent = a.opinions.filter((o) => o.stance === "no bearing");
                return (
                  <div key={i} className="border p-2.5 min-w-0 overflow-hidden"
                       style={{ borderColor: a.contested ? "#8a3324" : "#cfc6b0",
                                background: a.contested ? "#fdf6f4" : "transparent" }}>
                    {/* header: the trade facts */}
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-black text-[16px]" style={{ fontFamily: "Georgia, serif" }}>{a.symbol}</span>
                      <span className="text-[9px] uppercase tracking-widest px-1 rounded"
                            style={{ background: "#e6dfcc", color: "#4a4436" }}>{a.asset_class}</span>
                      {closed && <span className="text-[9px] uppercase tracking-widest font-bold" style={{ color: "#6b6355" }}>closed</span>}
                      {live && <span className="text-[9px] uppercase tracking-widest font-bold" style={{ color: "#8a3324" }}>real money</span>}
                      <span className="text-[10px]" style={{ color: "#8a8271" }}>{a.book}</span>
                      <span className={`ml-auto font-mono text-[13px] font-bold`}
                            style={{ color: (pnl ?? 0) >= 0 ? "#26689e" : "#8a3324" }}>{pnlTxt}</span>
                    </div>

                    {/* Where the position actually stands between its own two
                        limits — the fastest read of "how much room is left". */}
                    {!closed && a.entry != null && a.price != null && (a.stop != null || a.target != null) && (
                      <div className="mt-1.5">
                        <PositionLadder
                          entry={a.entry} price={a.price} stop={a.stop} target={a.target}
                          side={a.side} decimals={a.asset_class === "forex" ? 5 : 2} />
                      </div>
                    )}
                    <div className="text-[11px] font-mono mt-0.5 break-words" style={{ color: "#6b6355" }}>
                      {a.side}{a.qty != null ? ` x${a.qty}` : ""} · {(a.tranches?.length ?? 0) > 1 ? "avg " : ""}entry {a.entry}
                      {closed ? ` → exit ${a.exit}` : ` → now ${a.price ?? "—"}`}
                      {a.target ? ` · target ${a.target}` : (a.targets?.length ?? 0) > 1 ? ` · targets ${a.targets![0]}–${a.targets![a.targets!.length - 1]}` : ""}
                      {a.stop ? ` · stop ${a.stop}` : (a.stops?.length ?? 0) > 1 ? ` · stops ${a.stops![0]}–${a.stops![a.stops!.length - 1]}` : ""}
                      {a.pct_to_stop != null ? ` (${a.pct_to_stop}% away)` : ""}
                      {a.held_days != null ? ` · ${closed ? "held" : "oldest"} ${a.held_days}d` : ""}
                    </div>
                    {/* The tranches this asset is made of — lettered to match
                        the seats' qualifications below. One asset, its rungs. */}
                    {(a.tranches?.length ?? 0) > 0 && (
                      <div className="mt-1.5 space-y-0.5">
                        <div className="text-[9px] uppercase tracking-widest font-bold" style={{ color: "#8a8271" }}>
                          {a.tranches!.length} tranche{a.tranches!.length === 1 ? "" : "s"}{a.split ? " · the board splits by rung" : ""}
                        </div>
                        {a.tranches!.map((t, k) => {
                          const tp = closed ? t.realized_pnl : t.pnl_pct;
                          const tpTxt = closed
                            ? (t.realized_pnl != null ? signedUsd(t.realized_pnl) : "—")
                            : (t.pnl_pct != null ? `${t.pnl_pct >= 0 ? "+" : ""}${t.pnl_pct.toFixed(2)}%` : "—");
                          return (
                            <div key={k} className="flex flex-wrap gap-x-1.5 gap-y-0.5 text-[10.5px] font-mono items-baseline min-w-0" style={{ color: "#6b6355" }}>
                              <span className="font-bold" style={{ color: "#4a4436" }}>({String.fromCharCode(97 + k)})</span>
                              <span className="text-[9px] uppercase tracking-wider px-1 rounded" style={{ background: "#e6dfcc", color: "#4a4436" }}>{t.book}</span>
                              <span>{t.entry_rule}{t.strike != null ? ` @ ${t.strike}` : ""}</span>
                              <span>· {t.side} x{t.qty} @ {t.entry}{closed ? ` → ${t.exit}` : ""}</span>
                              {!closed && t.stop != null && <span>· stop {t.stop}{t.pct_to_stop != null ? ` (${t.pct_to_stop}%)` : ""}</span>}
                              {!closed && t.target != null && <span>· target {t.target}</span>}
                              {t.held_days != null && <span>· {t.held_days}d</span>}
                              <span className="ml-auto font-bold" style={{ color: (tp ?? 0) >= 0 ? "#26689e" : "#8a3324" }}>{tpTxt}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* The decision chain — auditable, not just asserted */}
                    {(a.thesis || a.regime_at_entry) && (
                      <div className="mt-1.5 pl-2 border-l-2 space-y-0.5" style={{ borderColor: "#cfc6b0" }}>
                        {a.opened_at && (
                          <div className="text-[9px] uppercase tracking-widest font-bold" style={{ color: "#8a8271" }}>
                            The chain · opened {a.opened_at.slice(0, 10)}
                            {a.closed_at ? ` → closed ${a.closed_at.slice(0, 10)}` : ""}
                          </div>
                        )}
                        {a.regime_at_entry && (
                          <div className="text-[11px]" style={{ color: "#6b6355" }}>
                            <span className="font-bold">Macro at entry:</span> {a.regime_at_entry}
                          </div>
                        )}
                        {a.thesis && (
                          <div className="text-[11.5px] italic" style={{ color: "#4a4436" }}>
                            <span className="font-bold not-italic">Thesis:</span> &ldquo;{a.thesis}&rdquo;
                          </div>
                        )}
                        {a.regime_at_exit && (
                          <div className="text-[11px]" style={{ color: "#6b6355" }}>
                            <span className="font-bold">Macro at exit:</span> {a.regime_at_exit}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Desk consensus at a glance — the relation between this
                        asset and the table, before the prose. */}
                    {a.opinions.length > 0 && (
                      <div className="mt-2 pt-1.5 border-t" style={{ borderColor: "#e6dfcc" }}>
                        <ConsensusStrip
                          supports={a.opinions.filter((o) => o.stance === "supports").length}
                          contradicts={a.opinions.filter((o) => o.stance === "contradicts").length}
                          silent={silent.length} />
                      </div>
                    )}

                    {/* the desks that had something to say */}
                    {spoke.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {spoke.map((o, j) => {
                          const s = STANCE[o.stance] ?? STANCE["no bearing"];
                          return (
                            <div key={j} className="flex gap-2 text-[12.5px]">
                              <span className="font-bold shrink-0" style={{ color: s.c }}>{s.mark}</span>
                              <div>
                                <span className="font-bold" style={{ fontFamily: "Georgia, serif" }}>{o.seat}</span>
                                <span className="text-[9px] uppercase tracking-widest ml-1.5" style={{ color: s.c }}>{s.label}</span>
                                <span className="leading-snug"> — {o.note}</span>
                                {/* Where this seat's view differs by rung: which
                                    tranche, which way, and why — the nuance that
                                    used to read as the board contradicting itself. */}
                                {(o.tranche_notes?.length ?? 0) > 0 && (
                                  <div className="mt-0.5 pl-2 border-l space-y-0.5" style={{ borderColor: "#e6dfcc" }}>
                                    {o.tranche_notes!.map((tn, k) => {
                                      const ts = STANCE[tn.stance] ?? STANCE["no bearing"];
                                      return (
                                        <div key={k} className="text-[11.5px] leading-snug">
                                          <span className="font-mono font-bold" style={{ color: "#4a4436" }}>({tn.letter})</span>
                                          <span className="text-[9px] uppercase tracking-wider ml-1 px-1 rounded" style={{ background: "#e6dfcc", color: "#4a4436" }}>{tn.book}</span>
                                          <span className="text-[9px] uppercase tracking-widest ml-1 font-bold" style={{ color: ts.c }}>{ts.mark} {ts.label}</span>
                                          <span> — {tn.note}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* explicit no-bearing roll-call: the traceability guarantee */}
                    {silent.length > 0 && (
                      <div className="text-[10.5px] mt-1.5 pt-1 border-t" style={{ borderColor: "#e6dfcc", color: "#9a9384" }}>
                        <span className="uppercase tracking-widest text-[9px] font-bold mr-1">No bearing</span>
                        {silent.map((o) => o.seat).join(" · ")}
                      </div>
                    )}
                    {a.opinions.length === 0 && (
                      <div className="text-[10.5px] mt-1.5" style={{ color: "#9a9384" }}>no desk filed on this position</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {ed?.trade_of_the_day && <TradeOfTheDaySection t={ed.trade_of_the_day} />}

        {(ed?.intel_charts?.market || (ed?.intel_cards?.length ?? 0) > 0) && (
          <div className="mt-6">
            <div className="text-center text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: "#6b6355" }}>Market intelligence · where the cost basis is heading</div>
            {ed?.intel_charts?.market && <img src={ed.intel_charts.market} alt="market structure" className="w-full rounded border" style={{ borderColor: "#d8d2c4" }} />}
            {(ed?.intel_cards ?? []).map(c => (
              <div key={c.symbol} className="mt-4">
                <div className="font-bold text-sm">{c.symbol} <span className="font-normal text-xs" style={{ color: "#6b6355" }}>· {c.shape} · structure {c.structure} / entry {c.entry}</span></div>
                {c.chart && <img src={c.chart} alt={`${c.symbol} story chart`} className="w-full rounded border mt-1" style={{ borderColor: "#d8d2c4" }} />}
                {c.commentary && <p className="text-[13px] leading-relaxed mt-1">{c.commentary}</p>}
                {c.prediction?.text && (
                  <p className="text-[13px] mt-1 pl-2" style={{ borderLeft: "3px solid #d9b24a" }}><b>Call:</b> {c.prediction.text}
                    <span className="block text-[11px]" style={{ color: "#6b6355" }}>Confirms: {c.prediction.confirms} · Contradicts: {c.prediction.contradicts}</span></p>
                )}
              </div>
            ))}
          </div>
        )}

        {ed?.angels_and_demons && (
          <div className="mt-4">
            <div className="text-center text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: "#6b6355" }}>
              Angels &amp; Demons · Stonky Argues With Himself
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="p-4 border" style={{ background: "#fbf8ee", borderColor: "#cfc6b0" }}>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#a68a2e" }}>
                  ☼ The Angel · Bull Case
                </div>
                <h3 className="text-lg font-black leading-tight my-1"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{ed.angels_and_demons.angel.headline}</h3>
                <p className="text-[13.5px] leading-relaxed" style={{ textAlign: "justify" }}>{ed.angels_and_demons.angel.body}</p>
              </div>
              <div className="p-4 border-2" style={{ background: "#1a1712", borderColor: "#1a1712", color: "#f5f1e6" }}>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#c0463a" }}>
                  ☠ The Demon · Bear Case
                </div>
                <h3 className="text-lg font-black leading-tight my-1"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{ed.angels_and_demons.demon.headline}</h3>
                <p className="text-[13.5px] leading-relaxed" style={{ textAlign: "justify" }}>{ed.angels_and_demons.demon.body}</p>
              </div>
            </div>
          </div>
        )}

        {ed?.gpop_corner?.headline && (
          <div className="mt-4 p-4 border" style={{ background: "#f3efe1", borderColor: "#cfc6b0" }}>
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#4a5a3a" }}>
                G-Pop&rsquo;s Corner · The Honest Numbers
              </div>
              {ed.gpop_corner.told_you_so && (
                <div className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border"
                     style={{ color: "#8a3324", borderColor: "#8a3324" }}>
                  Told You So
                </div>
              )}
            </div>
            <h3 className="text-lg font-black leading-tight my-1"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{ed.gpop_corner.headline}</h3>
            <p className="text-[13.5px] leading-relaxed" style={{ textAlign: "justify" }}>{ed.gpop_corner.body}</p>
            <div className="text-right text-xs font-bold mt-1">— G-Pop</div>
          </div>
        )}

        {ed?.stonky_take?.headline && (
          <div className="mt-4 p-4 border-2 border-black" style={{ background: "#efe9d8" }}>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#8a3324" }}>Stonky&rsquo;s Take · Opinion</div>
            <h3 className="text-xl font-black leading-tight my-1"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{ed.stonky_take.headline}</h3>
            <p className="text-[14px] leading-relaxed italic" style={{ textAlign: "justify" }}>
              <Cited text={ed.stonky_take.body} sources={ed.stonky_take.sources} />
            </p>
            <div className="text-right text-xs font-bold mt-1">— Stonky, at the head of the table</div>
            <SourcedFooter s={ed.stonky_take.sources} />
          </div>
        )}

        <LegendBox legend={ed?.source_manifest?.legend} />

        <div className="text-center text-[10px] uppercase tracking-widest mt-4" style={{ color: "#8a8271" }}>
          Written from the firm&rsquo;s own feeds · corroborated where stated · not investment advice
        </div>
      </div>
    </div>
  );
}

function firstLetter(s: string): string { return (s || "").trim().charAt(0) || ""; }
function dropCap(s: string): string { return (s || "").trim().slice(1); }
